import moment from "moment";
import React, { useEffect, useState } from "react";
import {
  FaCarAlt,
  FaPlusCircle,
  FaWallet,
  FaIdCard,
  FaCalendarAlt,
  FaSearch,
} from "react-icons/fa";
import api from "../../setupMiddleware";
import useApiLoader from "../../Utils/useApiLoader";
import DlCardPage from "../Common/DLCard";
import Loader from "../Loader/Loader";
import { Link } from "react-router-dom";
import { useWallet } from "../../Context/WalletContext";
import { ToastContainer, toast, Bounce } from "react-toastify";

export default function GetDl() {
  const loading = useApiLoader();
  const { refreshBalance } = useWallet();
  const [transactions, setTransactions] = useState([]);
  const [dlNumber, setDlNumber] = useState("");
  const [dob, setDob] = useState("");
  const [dlData, setDlData] = useState();
  const [downloadFunc, setDownloadFunc] = useState(null);

  const fetchTransaction = async () => {
    try {
      const res = await api.get("/api/transactions?request_type=DL");
      if (res.status === 200) {
        setTransactions(res.data?.transactions);
      }
    } catch (err) {
      console.error("Error in fetching transactions", err);
    }
  };

  const findDlData = async () => {
    try {
      const res = await api.post("/api/dl/fetch", {
        dl_number: dlNumber,
        dob: dob,
      });
      if (res.status === 200) {
        let dl_data = {
          ...res.data?.dl_data,
          dlNumber: dlNumber,
        };
        setDlData(dl_data);
        fetchTransaction();
        refreshBalance();
        toast.success(res.data?.message, {
          position: "top-left",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "colored",
          transition: Bounce,
        });
      }
    } catch (error) {
      console.log("Error", error);
      if (error?.status === 500) {
        toast.error(error.response?.data?.message, {
          position: "top-left",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "colored",
          transition: Bounce,
        });
      } else {
        toast.warning(error.response?.data?.message, {
          position: "top-left",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "colored",
          transition: Bounce,
        });
      }
    }
  };

  useEffect(() => {
    if (dlData && downloadFunc) {
      // Ensure DOM updates first
      setTimeout(() => downloadFunc(), 50);
    }
  }, [dlData, downloadFunc]);

  useEffect(() => {
    fetchTransaction();
  }, []);

  const today = new Date().toISOString().split("T")[0];

  return (
    <>
      {loading && <Loader opacity={50} />}
      <div className="text-gray-800 relative">
        {/* Main Content */}
        <main className="max-w-5xl mx-auto py-3 px-3">
          {/* Search Section */}
          <div className="bg-white rounded-xl p-5 shadow-sm mt-1 md:mt-6">
            <div className="flex flex-col md:flex-row items-center gap-3 mb-5">
              {/* DL Number */}
              <div className="relative w-full">
                <FaIdCard className="absolute left-3 top-1/2 -translate-y-1/2 text-brand/70 text-base" />
                <input
                  type="text"
                  value={dlNumber}
                  onChange={(e) =>
                    setDlNumber(String(e.target.value).toUpperCase())
                  }
                  placeholder="Enter Driving License Number"
                  className="w-full pl-10 pr-4 h-[44px] bg-white/90 border border-gray-200 rounded-lg text-gray-800 text-xs md:text-sm
                 shadow-[inset_0_1px_2px_rgba(255,255,255,0.6),_0_1px_3px_rgba(0,0,0,0.08)]
                 focus:border-brand focus:ring-2 focus:ring-brand/30 outline-none transition"
                />
              </div>

              {/* DOB Field */}
              <div className="relative w-full md:max-w-[200px]">
                <FaCalendarAlt className="absolute left-3 top-1/2 -translate-y-1/2 text-brand/70 text-base" />

                <input
                  type={dob ? "date" : "text"} // key trick 🔥
                  placeholder="Date of Birth"
                  value={dob}
                  max={today}
                  onChange={(e) => setDob(e.target.value)}
                  onFocus={(e) => (e.target.type = "date")}
                  onBlur={(e) => {
                    if (!dob) e.target.type = "text";
                  }}
                  className="w-full pl-10 pr-3 h-[44px] bg-white/90 border border-gray-200 rounded-lg text-gray-800 text-xs md:text-sm
               shadow-[inset_0_1px_2px_rgba(255,255,255,0.6),_0_1px_3px_rgba(0,0,0,0.08)]
               focus:border-brand focus:ring-2 focus:ring-brand/30 outline-none transition"
                />
              </div>

              {/* Button */}
              <button
                onClick={findDlData}
                className="h-[44px] inline-flex items-center justify-center gap-2 whitespace-nowrap 
               bg-brand text-white px-5 rounded-lg text-xs md:text-sm font-medium shadow-sm 
               hover:bg-brand/90 active:scale-[.98] transition-all w-full md:w-auto"
              >
                <FaSearch className="text-base leading-none" />
                <span className="leading-none">Get DL</span>
              </button>
            </div>

            <div className="flex gap-3">
              <a
                href={"/KL5411XXXXXXXX.pdf"}
                target="_blank"
                className="flex-1 text-center bg-indigo-100 text-indigo-700 py-2 rounded-lg hover:bg-indigo-200 transition"
              >
                DL Card Sample
              </a>
            </div>
          </div>

          {/* Recent Transactions */}
          <div className="bg-white/95 backdrop-blur-md border border-gray-100 rounded-2xl shadow-[inset_0_1px_1px_rgba(255,255,255,0.8),_0_2px_6px_rgba(0,0,0,0.05)] mt-6 overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-3 bg-gray-50/60 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-brand/10 text-brand shadow-inner">
                  <FaWallet className="w-4 h-4" />
                </div>
                <h2 className="font-semibold text-gray-800 text-xs md:text-sm tracking-wide">
                  Recent DL Transactions
                </h2>
              </div>
              <Link
                to={"/all-transactions"}
                className="text-xs md:text-sm text-brand font-medium hover:underline"
              >
                View All
              </Link>
            </div>

            {/* Transactions List */}
            <div className="p-5 divide-y divide-gray-100">
              {transactions.length > 0 ? (
                transactions.slice(0, 5).map((tx) => (
                  <div
                    key={tx.id}
                    className="flex justify-between items-center py-3 transition-all hover:bg-gray-50 rounded-lg px-2"
                  >
                    {/* Left Section */}
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-9 h-9 flex items-center justify-center rounded-xl ${
                          tx.type === "credit"
                            ? "bg-green-100 text-green-600"
                            : "bg-rose-100 text-rose-600"
                        } shadow-inner`}
                      >
                        {tx.type === "credit" ? (
                          <FaPlusCircle className="w-5 h-5" />
                        ) : (
                          <FaCarAlt className="w-5 h-5" />
                        )}
                      </div>

                      <div>
                        <p className="font-medium text-gray-800 text-xs md:text-sm">
                          {tx.type === "debit"
                            ? `${tx.request_type} ${tx.transaction_name}`
                            : tx.transaction_name}
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {moment(new Date(tx.transaction_time)).format(
                            "DD MMM YYYY, hh:mm A"
                          )}
                        </p>
                      </div>
                    </div>

                    {/* Right: Amount */}
                    <span
                      className={`font-semibold text-xs md:text-sm px-3 py-1.5 rounded-full ${
                        tx.type === "credit"
                          ? "text-green-700 bg-green-50"
                          : "text-rose-600 bg-rose-50"
                      }`}
                    >
                      {tx.type === "credit"
                        ? `+ ₹${Number(tx.amount).toLocaleString("en-IN")}`
                        : `- ₹${Math.abs(tx.amount).toLocaleString("en-IN")}`}
                    </span>
                  </div>
                ))
              ) : (
                <div className="text-center py-5 text-gray-400 text-xs md:text-sm">
                  No recent transactions
                </div>
              )}
            </div>
          </div>
          <DlCardPage
            dlData={dlData}
            apiTriggerDownload={(fn) => setDownloadFunc(() => fn)}
          />
          <ToastContainer />
        </main>
      </div>
    </>
  );
}
